import type { IOrder, IPrimer } from '../../Model/index.ts'
import type { FileForProcessing } from '../../Model/Sample/ISample.ts'
import type { LengthCheck, LengthWindow } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.ts'
import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.ts'
import type { ReadLengthSummary } from '../../Util/orderSettingDetection/readLengths.ts'
import { shareOutside, shortenedBy } from '../../Util/orderSettingDetection/readLengths.ts'

/* keep in sync with airflow-dags dags/tasks/quality_filtering.py */
const ONT_MIN_FALLBACK = 100
const ONT_MAX_FALLBACK = 600
const ILLUMINA_MIN_FALLBACK: Partial<Record<string, number>> = { CO1_ANIMAL: 200, '12S_FISH': 95 }
const ILLUMINA_OTHER_MIN_FALLBACK = 150
const ILLUMINA_CO1_MAX_FALLBACK = 315

/** The window the pipeline's quality filter applies to a sample carrying `set`; an unset (or zero) bound takes the pipeline's fallback. */
export const filterWindow = (
  platform: SequencingPlatformKey | null,
  taxon: string | undefined,
  set: { min?: number | null; max?: number | null }
): LengthWindow => {
  if (platform === 'ONT') {
    return { min: set.min || ONT_MIN_FALLBACK, max: set.max || ONT_MAX_FALLBACK }
  }
  const min = set.min || (taxon && ILLUMINA_MIN_FALLBACK[taxon]) || ILLUMINA_OTHER_MIN_FALLBACK
  return taxon === 'CO1_ANIMAL' ? { min, max: set.max || ILLUMINA_CO1_MAX_FALLBACK } : { min }
}

const MARGIN = 0.1
const ROUND_TO = 10

/** p2 − 10 % to p98 + 10 %, rounded outwards to 10 bp. */
export const measuredWindow = (lengths: ReadLengthSummary): LengthWindow => ({
  min: Math.floor((lengths.centiles[2] * (1 - MARGIN)) / ROUND_TO) * ROUND_TO,
  max: Math.ceil((lengths.centiles[98] * (1 + MARGIN)) / ROUND_TO) * ROUND_TO
})

/**
 * Trimmed ONT reads are the amplicon as sequenced, and the ONT filter enforces both bounds: the window
 * comes from the reads rather than the registry, whose range may not be what the lab amplified.
 */
export const measuredWindowFor = (
  platform: SequencingPlatformKey | null,
  sampleConfiguration: SampleConfigurationTypeKey,
  lengths: ReadLengthSummary | undefined
): LengthWindow | undefined =>
  platform === 'ONT' && sampleConfiguration === 'MERGED_AND_TRIMMED_SAMPLES' && lengths ? measuredWindow(lengths) : undefined

const MERGED: readonly SampleConfigurationTypeKey[] = ['MERGED_SAMPLES', 'MERGED_AND_TRIMMED_SAMPLES']

/** Above this share of reads outside the window, the review warns. */
export const OUTSIDE_LIMIT = 0.2

const sameWindow = (a: LengthWindow, b: LengthWindow) => a.min === b.min && a.max === b.max

const mostCommonWindow = (items: readonly FileForProcessing[]) => {
  const counts = new Map<string, { set: { min?: number; max?: number }; count: number }>()
  for (const item of items) {
    const key = `${item.minBasePairLength ?? ''}-${item.maxBasePairLength ?? ''}`
    const seen = counts.get(key)
    counts.set(key, { set: { min: item.minBasePairLength, max: item.maxBasePairLength }, count: (seen?.count ?? 0) + 1 })
  }
  return [...counts.values()].sort((a, b) => b.count - a.count)[0]?.set ?? {}
}

/**
 * How the sampled read lengths of merged or single-end files fit the window the samples will carry and
 * the one the chosen primer pair expects. Untrimmed reads are judged as trimming leaves them, without
 * their primers. Paired mates are not amplicon-length, so they are not judged.
 */
export const checkReadLengths = ({
  lengths,
  order,
  platform,
  primer,
  items
}: {
  lengths: ReadLengthSummary | undefined
  order: Pick<IOrder, 'sampleConfiguration' | 'targetTaxonomy'>
  platform: SequencingPlatformKey | null
  primer: IPrimer | 'SKIPPED'
  /** Every item as submit will leave it. */
  items: readonly FileForProcessing[]
}): LengthCheck | undefined => {
  const sample = items.find((item) => item.itemType === 'SAMPLE') ?? items[0]
  if (!lengths || !sample || !MERGED.includes(order.sampleConfiguration)) {
    return undefined
  }
  const primerBases =
    order.sampleConfiguration === 'MERGED_SAMPLES' ? (sample.forwardPrimer?.length ?? 0) + (sample.reversePrimer?.length ?? 0) : 0
  const trimmed = shortenedBy(lengths, primerBases)
  const taxon = order.targetTaxonomy
  const expected = filterWindow(platform, taxon, primer === 'SKIPPED' ? {} : { min: primer.basePairLength, max: primer.maxBasePairLength })
  const written = filterWindow(platform, taxon, mostCommonWindow(items))
  const measured = measuredWindowFor(platform, order.sampleConfiguration, lengths)
  return {
    median: trimmed.centiles[50],
    expected,
    outsideExpected: shareOutside(trimmed, expected.min, expected.max),
    written,
    outsideWritten: shareOutside(trimmed, written.min, written.max),
    measured: !!measured && sameWindow(measured, written)
  }
}

const range = (window: LengthWindow) => (window.max === undefined ? `at least ${window.min} bp` : `${window.min}–${window.max} bp`)
const percent = (share: number) => `${Math.round(share * 100)}%`

/* deliberately loose: only reads that cannot be this pair's amplicon */
const LONGER_THAN_AMPLICON = 1.5
const SHORTER_THAN_AMPLICON = 0.6

/** Whether typical reads are far off the pair's registered amplicon length. */
export const ampliconMismatch = (median: number, primer: IPrimer): 'longer' | 'shorter' | undefined => {
  if (median > LONGER_THAN_AMPLICON * (primer.maxBasePairLength || primer.basePairLength)) {
    return 'longer'
  }
  return median < SHORTER_THAN_AMPLICON * primer.basePairLength ? 'shorter' : undefined
}

/** What the review says about the read lengths: reads that cannot be the chosen pair's amplicon, or too many outside a window. */
export const lengthWarning = (check: LengthCheck, primer: IPrimer | 'SKIPPED'): string | undefined => {
  const reads = `Most reads are ~${check.median} bp`
  const discard =
    check.outsideWritten > OUTSIDE_LIMIT ? `the pipeline would discard ${percent(check.outsideWritten)} of the reads` : undefined
  if (primer === 'SKIPPED') {
    return discard && `${reads}, but the samples’ length filter keeps ${range(check.written)}; ${discard}.`
  }
  const pair = `the chosen primer pair (${primer.forwardName} / ${primer.reverseName})`
  const off = ampliconMismatch(check.median, primer)
  if (!off && check.outsideExpected <= OUTSIDE_LIMIT) {
    return discard && `${reads}, but the samples’ length filter keeps ${range(check.written)}; ${discard}.`
  }
  const amplicon = primer.maxBasePairLength ? `${primer.basePairLength}–${primer.maxBasePairLength}` : `${primer.basePairLength}`
  const head = off
    ? `${reads}, much ${off} than the ~${amplicon} bp amplicon of ${pair}`
    : `${reads}, but ${pair} expects ${range(check.expected)}`
  if (discard) {
    const filter = sameWindow(check.written, check.expected) ? '' : ` with the samples’ length filter (${range(check.written)})`
    return `${head}; ${discard}${filter}. Check the primer pair.`
  }
  if (off) {
    return `${head}. Check the primer pair.`
  }
  return check.measured
    ? `${head}. Length filtering uses the measured ${range(check.written)} instead; check the primer pair.`
    : `${head}. The samples’ length filter (${range(check.written)}) fits the reads; check the primer pair.`
}
